import React, { useState, useRef } from 'react';
import { Box, Typography, Button, FormControl, InputLabel, Select, MenuItem, TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, Skeleton, Chip, LinearProgress, Grid, Switch, CircularProgress, IconButton, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DepartmentFormModal from '../../../components/DepartmentFormModal';
import ExportFormatModal from '../../../components/ExportFormatModal';
import { StaffService } from '../../../api/services/staffService';
import { exportClientSide } from '../../../utils/exportUtils';
import ReplayIcon from '@mui/icons-material/Replay';
import { standardFilterSelectSx, standardResetButtonSx, standardSecondaryButtonSx } from '../../../components/common/tableStyles';

export default function DepartmentsTab({
    departments,
    setDepartments,
    isLoadingDepts,
    exportToCSV,
    handleSuccess,
    setNotification,
    staffMembers,
    leaveRequests
}) {
    const [deptReportEmploymentFilter, setDeptReportEmploymentFilter] = useState('All');
    const [isDeptModalOpen, setIsDeptModalOpen] = useState(false);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [isDeleteDeptOpen, setIsDeleteDeptOpen] = useState(false);
    const [deptToDelete, setDeptToDelete] = useState(null);
    const [isDeletingDept, setIsDeletingDept] = useState(false);
    const [updatingDeptId, setUpdatingDeptId] = useState(null);
    const [isSavingOrder, setIsSavingOrder] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);

    const dragItem = useRef();
    const dragOverItem = useRef();

    const handleSort = async () => {
        if (dragItem.current === undefined || dragOverItem.current === undefined || dragItem.current === dragOverItem.current) return;
        
        const _departments = [...departments];
        const draggedItemContent = _departments.splice(dragItem.current, 1)[0];
        _departments.splice(dragOverItem.current, 0, draggedItemContent);
        
        dragItem.current = undefined;
        dragOverItem.current = undefined;
        
        const originalDepartments = [...departments];
        setDepartments(_departments);
        setIsSavingOrder(true);
        
        try {
            const departmentIds = _departments.map(d => d.id || d._id);
            await StaffService.reorderDepartments(departmentIds);
            setNotification({ open: true, message: 'Order saved', severity: 'success' });
        } catch (err) {
            setDepartments(originalDepartments);
            setNotification({ open: true, message: 'Failed to save order. Reverting.', severity: 'error' });
        } finally {
            setIsSavingOrder(false);
        }
    };

    const handleOpenDeptModal = (dept = null) => {
        setSelectedDepartment(dept);
        setIsDeptModalOpen(true);
    };

    const handleDeleteDepartment = async () => {
        if (!deptToDelete) return;
        setIsDeletingDept(true);
        try {
            await StaffService.deleteDepartment(deptToDelete.id || deptToDelete._id);
            setNotification({ open: true, message: 'Department deleted successfully', severity: 'success' });
            setDepartments(prev => prev.filter(d => (d.id || d._id) !== (deptToDelete.id || deptToDelete._id)));
            setIsDeleteDeptOpen(false);
            setDeptToDelete(null);
        } catch (err) {
            setNotification({ 
                open: true, 
                message: err.message || 'Failed to delete department. It may have active staff.', 
                severity: 'error' 
            });
        } finally {
            setIsDeletingDept(false);
        }
    };

    const handleDepartmentStatusChange = async (dept, newStatus) => {
        const id = dept.id || dept._id;
        setUpdatingDeptId(id);
        try {
            await StaffService.updateDepartmentStatus(id, newStatus);
            setNotification({ open: true, message: 'Department status updated', severity: 'success' });
            setDepartments(prev => prev.map(d => (d.id || d._id) === id ? { ...d, status: newStatus } : d));
        } catch (err) {
            setNotification({ 
                open: true, 
                message: err.message || 'Failed to update department status.', 
                severity: 'error' 
            });
        } finally {
            setUpdatingDeptId(null);
        }
    };

    // Calculate Department-wise Staff Count Report
    const departmentStats = departments.map(dept => {
        let staffInDept = staffMembers.filter(s => (s.department?.id || s.department?._id || s.department) === (dept.id || dept._id) || s.department?.name === dept.name);
        if (deptReportEmploymentFilter !== 'All') {
            staffInDept = staffInDept.filter(s => (s.employmentType || s.type || 'Full-time') === deptReportEmploymentFilter);
        }
        
        const totalStaff = staffInDept.length;
        const activeStaff = staffInDept.filter(s => s.status !== 'Inactive').length;
        const onDuty = staffInDept.filter(s => s.onDuty).length;
        
        const d = new Date();
        const todayStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const onLeave = leaveRequests.filter(l => 
            l.status === 'Approved' && 
            (l.startDate <= todayStr && (l.endDate || l.startDate) >= todayStr) &&
            staffInDept.some(s => (s.id || s._id) === (l.staffId || l.staff?.id || l.staff?._id))
        ).length;

        const vacantPositions = Math.max(0, 5 - activeStaff);

        return {
            ...dept,
            totalStaff,
            activeStaff,
            onDuty,
            onLeave,
            vacantPositions,
            onDutyPercent: totalStaff > 0 ? (onDuty / totalStaff) * 100 : 0
        };
    });

    const handleExport = (format) => {
        setExportModalOpen(false);
        try {
            exportClientSide(format, departmentStats, 'department_staff_count');
            handleSuccess('Export successful.');
        } catch (error) {
            setNotification({ open: true, message: error.message || 'Export failed.', severity: 'error' });
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'flex-end' }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="h6" fontWeight="bold">Department Staffing Overview</Typography>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select
                            displayEmpty
                            value={deptReportEmploymentFilter}
                            onChange={(e) => setDeptReportEmploymentFilter(e.target.value)}
                            sx={standardFilterSelectSx}
                        >
                            <MenuItem value="All">All Types</MenuItem>
                            <MenuItem value="Full-time">Full-time</MenuItem>
                            <MenuItem value="Part-time">Part-time</MenuItem>
                            <MenuItem value="Contractor">Contractor</MenuItem>
                        </Select>
                    </FormControl>
                    {deptReportEmploymentFilter !== 'All' && (
                        <Button variant="outlined" startIcon={<ReplayIcon sx={{ fontSize: 16 }} />} sx={standardResetButtonSx} onClick={() => setDeptReportEmploymentFilter('All')}>Reset</Button>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {isSavingOrder && <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mr: 2 }}><CircularProgress size={12} sx={{ mr: 1 }} /> Saving order...</Typography>}
                    <Button variant="outlined" startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />} sx={standardSecondaryButtonSx} onClick={() => setExportModalOpen(true)}>Export Options</Button>
                    <ExportFormatModal
                        open={exportModalOpen}
                        onClose={() => setExportModalOpen(false)}
                        onExport={handleExport}
                        isExporting={false}
                        title="Export Department Stats"
                    />
                </Box>
            </Box>

            <TableContainer component={Paper} sx={{ mb: 4 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Department</TableCell>
                            <TableCell align="center">Total Staff</TableCell>
                            <TableCell align="center">Active Staff</TableCell>
                            <TableCell align="center">On Duty Today</TableCell>
                            <TableCell align="center">On Leave Today</TableCell>
                            <TableCell align="center">Vacant Roles</TableCell>
                            <TableCell sx={{ minWidth: 150 }}>Duty Coverage</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoadingDepts ? (
                            <TableRow>
                                <TableCell colSpan={7}><Skeleton variant="rectangular" height={60} /></TableCell>
                            </TableRow>
                        ) : departmentStats.length > 0 ? (
                            departmentStats.map((stat) => (
                                <TableRow key={stat.id || stat._id} hover>
                                    <TableCell><b>{stat.name}</b></TableCell>
                                    <TableCell align="center"><Chip label={stat.totalStaff} size="small" /></TableCell>
                                    <TableCell align="center"><Chip label={stat.activeStaff} size="small" color="primary" variant="outlined" /></TableCell>
                                    <TableCell align="center"><Chip label={stat.onDuty} size="small" color="success" variant="outlined" /></TableCell>
                                    <TableCell align="center"><Chip label={stat.onLeave} size="small" color="warning" variant="outlined" /></TableCell>
                                    <TableCell align="center"><Chip label={stat.vacantPositions} size="small" color="error" variant="outlined" /></TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Box sx={{ width: '100%', mr: 1 }}>
                                                <LinearProgress variant="determinate" value={stat.onDutyPercent} color="success" sx={{ height: 6, borderRadius: 3 }} />
                                            </Box>
                                            <Box sx={{ minWidth: 35 }}>
                                                <Typography variant="body2" color="text.secondary">{Math.round(stat.onDutyPercent)}%</Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>No departments configured.</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Department Management</Typography>
            <Grid container spacing={3}>
                {isLoadingDepts ? (
                    Array.from(new Array(3)).map((_, index) => (
                        <Grid item xs={12} sm={6} md={4} key={`skeleton-dept-${index}`}>
                            <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 3 }} />
                        </Grid>
                    ))
                ) : departments.length > 0 ? (
                    departments.map((dept, index) => (
                        <Grid 
                            item xs={12} sm={6} md={4} 
                            key={dept.id || dept._id}
                            draggable
                            onDragStart={() => dragItem.current = index}
                            onDragEnter={() => dragOverItem.current = index}
                            onDragEnd={handleSort}
                            onDragOver={(e) => e.preventDefault()}
                            sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
                        >
                            <Paper sx={{ p: 3, borderRadius: 3, height: '100%', position: 'relative', border: '1px solid #eee' }}>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>{dept.name}</Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Code: {dept.code} | Cost Center: {dept.costCenterCode}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {dept.description || 'No description provided'}
                                </Typography>
                                <Box sx={{ mt: 'auto', pt: 2, display: 'flex', alignItems: 'center' }}>
                                    <Switch 
                                        size="small" 
                                        checked={dept.status !== 'Inactive'} 
                                        onChange={(e) => handleDepartmentStatusChange(dept, e.target.checked ? 'Active' : 'Inactive')}
                                        disabled={updatingDeptId === (dept.id || dept._id)}
                                        color="success"
                                    />
                                    <Typography variant="caption" color={dept.status !== 'Inactive' ? 'success.main' : 'text.secondary'} sx={{ ml: 1, fontWeight: 'bold' }}>
                                        {updatingDeptId === (dept.id || dept._id) ? <CircularProgress size={10} sx={{ mr: 1 }} /> : null}
                                        {dept.status !== 'Inactive' ? 'Active' : 'Inactive'}
                                    </Typography>
                                </Box>
                                <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
                                    <IconButton 
                                        size="small"
                                        onClick={() => handleOpenDeptModal(dept)}
                                        sx={{ color: 'text.secondary' }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton 
                                        size="small"
                                        onClick={() => { setDeptToDelete(dept); setIsDeleteDeptOpen(true); }}
                                        sx={{ color: 'error.main' }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Paper>
                        </Grid>
                    ))
                ) : (
                    <Grid item xs={12}>
                        <Alert severity="info">No departments found. You can add one below.</Alert>
                    </Grid>
                )}
                <Grid item xs={12} sm={6} md={4}>
                    <Paper 
                        sx={{ p: 3, borderRadius: 3, border: '2px dashed #eee', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 140, cursor: 'pointer', '&:hover': { bgcolor: 'rgba(0,0,0,0.01)' } }}
                        onClick={() => handleOpenDeptModal(null)}
                    >
                        <AddIcon color="action" sx={{ mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">Add New Department</Typography>
                    </Paper>
                </Grid>
            </Grid>

            <DepartmentFormModal
                open={isDeptModalOpen}
                onClose={() => setIsDeptModalOpen(false)}
                onSuccess={handleSuccess}
                departmentData={selectedDepartment}
                staffMembers={staffMembers}
            />

            <Dialog open={isDeleteDeptOpen} onClose={() => !isDeletingDept && setIsDeleteDeptOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold', color: 'error.main' }}>Delete Department</DialogTitle>
                <DialogContent dividers>
                    <Typography>
                        Are you sure you want to delete the <b>{deptToDelete?.name}</b> department? 
                        This action cannot be undone and may affect staff members currently assigned to it.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setIsDeleteDeptOpen(false)} disabled={isDeletingDept}>Cancel</Button>
                    <Button 
                        onClick={handleDeleteDepartment} 
                        color="error" 
                        variant="contained"
                        disabled={isDeletingDept}
                        startIcon={isDeletingDept && <CircularProgress size={16} color="inherit" />}
                    >
                        {isDeletingDept ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
